import React, { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import QrCode from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as CryptoBackIcon } from '@pkg/assets/icons/v2/crypto_back.svg';
import YatFloatingInput from '@pkg/components/Input/FloatingInput';
import { useAppDispatch, useAppSelector, useLarge } from '@pkg/hooks';
import { CryptoType, PaymentCryptoStep } from '@pkg/types';

import { apiFactory } from '@app/client';
import { CartStatus, type CryptoPaymentData } from '@app/client/common/structs/orders/cart';
import { Cart, Orders } from '@app/client/resources';
import { YAT_PICKER } from '@app/routes/paths';
import { setPurchasedAmount } from '@app/store/actions/crypto';
import { setReduxCartItems } from '@app/store/actions/picker';
import { addAlert } from '@app/store/slices';

interface CryptoStepSendPaymentProps {
    onBack: () => void;
    onCancel: () => void;
    onNext: (step: PaymentCryptoStep) => void;
    onFinish: () => void;
    cryptoType: CryptoType;
}

const paymentDataInitialState: CryptoPaymentData = { title: '', amount: 0, address: '', currency: '' };

export const CryptoStepSendPayment = ({
    onBack,
    onCancel,
    cryptoType,
    onNext,
    onFinish,
}: CryptoStepSendPaymentProps) => {
    const { t } = useTranslation(['yat_picker']);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [curPaymentMethodData, setCurPaymentMethodData] = useState<CryptoPaymentData>(paymentDataInitialState);
    const [pollingId, setPollingId] = useState(0);
    const { paymentMethodsData } = useAppSelector((rootState) => rootState.crypto);
    const { orderId, orderItems } = useAppSelector((rootState) => rootState.picker);
    const { isLarge } = useLarge();

    const formatAmount = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const { target } = event;
            if (!curPaymentMethodData) {
                return;
            }
            if (!target.value) {
                setCurPaymentMethodData((d) => ({
                    ...d,
                    amount: 0,
                }));
            } else {
                setCurPaymentMethodData((d) => ({
                    ...d,
                    amount: +target.value || 0,
                }));
            }
        },
        [curPaymentMethodData],
    );

    const qrCode = useMemo(() => {
        if (!curPaymentMethodData) {
            return '';
        }
        const title = curPaymentMethodData.title === 'usdc' ? 'ethereum' : curPaymentMethodData.title;
        return `${title}:${curPaymentMethodData.address}?amount=${curPaymentMethodData.amount}&value=${curPaymentMethodData.amount}`;
    }, [curPaymentMethodData]);

    const qrSize = useMemo(() => {
        if (isLarge) return 168;
        return 220;
    }, [isLarge]);

    useEffect(() => {
        setCurPaymentMethodData(paymentMethodsData.find((d) => d.currency === cryptoType) || paymentDataInitialState);
    }, [paymentMethodsData, cryptoType]);

    useEffect(() => {
        let isSubscribed = true;
        apiFactory()
            .call(Orders.getOrder, { id: orderId })
            .then(async (r) => {
                if (!isSubscribed) {
                    return;
                }
                if (r.ok) {
                    const {
                        status,
                        remaining_due_in_cents,
                        total_in_cents,
                        payment_method_data,
                        seconds_until_expiry,
                    } = r.value;
                    if (!seconds_until_expiry) {
                        const res = await apiFactory().call(Cart.convertToDraft, {});
                        if (res.ok) {
                            dispatch(setReduxCartItems(res.value));
                        }
                        navigate(YAT_PICKER, {
                            state: { afterDraftConvert: true, enableGoBack: true },
                        });
                    }
                    const paymentData = payment_method_data.methods.find((d) => d.currency === cryptoType);
                    paymentData && setCurPaymentMethodData(paymentData);
                    if (status === CartStatus.Paid) {
                        onFinish();
                    } else if (status === CartStatus.PendingPayment) {
                        if (remaining_due_in_cents < total_in_cents) {
                            dispatch(
                                setPurchasedAmount({
                                    amount: total_in_cents / 100,
                                    actualAmount: (total_in_cents - remaining_due_in_cents) / 100,
                                }),
                            );
                            onNext(PaymentCryptoStep.notEnough);
                        }
                    }
                } else {
                    dispatch(addAlert({ message: r.error.detail || '' }));
                }
            })
            .finally(() => {
                if (!isSubscribed) {
                    return;
                }
                setTimeout(() => {
                    setPollingId((pollingId) => pollingId + 1);
                }, 2000);
            });

        return () => {
            isSubscribed = false;
        };
    }, [pollingId, cryptoType, dispatch, navigate, onFinish, onNext, orderId]);

    const cryptoInstructionUnit =
        cryptoType === CryptoType.USDC ? `${cryptoType} ${t('pay_usdc_explainer')}` : cryptoType;

    return (
        <div className="yat-picker-checkout__sidebar-vertical full">
            <div className="yat-picker-checkout__sidebar-back" onClick={onBack}>
                <CryptoBackIcon />
            </div>
            <div className="yat-picker-checkout__sidebar-heading">
                <h2 className="yat-picker-checkout__sidebar-cryptotitle">{t('pay_crypto_send_title')}</h2>
                <p className="yat-picker-checkout__sidebar-cryptoinstruct">
                    {t('pay_crypto_send_instruction', {
                        unit: cryptoInstructionUnit,
                        term: orderItems.length > 1 ? 'Yats' : 'Yat',
                    })}
                </p>
            </div>
            {qrCode && (
                <div className="yat-picker-checkout__sidebar-qrcode">
                    <QrCode value={qrCode} size={qrSize} />
                </div>
            )}
            <div>
                <div className="yat-picker-checkout__sidebar-cryptofield">
                    <YatFloatingInput
                        label="Amount"
                        value={curPaymentMethodData?.amount || 0}
                        onChange={(event) => formatAmount(event)}
                        type="number"
                    />
                </div>
                <div className="yat-picker-checkout__sidebar-cryptofield">
                    <YatFloatingInput
                        placeholder={`Input your ${cryptoType} address`}
                        label={`${cryptoType} Address`}
                        value={curPaymentMethodData?.address || ''}
                        readOnly
                    />
                </div>
            </div>
            <div className="yat-picker-checkout__sidebar-alternative">
                <span onClick={onCancel}>{t('pay_crypto_alternative')}</span>
            </div>
            <div className="yat-picker-checkout__sidebar-cryptofooter">{t('coinbase_footer')}</div>
        </div>
    );
};
